import React, { useState } from 'react';
import { Search, X, ExternalLink, Filter, Clock, Plane, MapPin, ArrowRight } from 'lucide-react';
import Logo from '../../components/Logo';
import Footer from '../../components/Footer';
import ThemeToggle from '../../components/ThemeToggle';
import WalletMenu from '../../components/WalletMenu';
import NavigationMenu from '../../components/NavigationMenu';

interface NFT {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  flightNumber: string;
  origin: string;
  destination: string;
  date: string;
  duration: string;
  aircraft: string;
  price: number;
  currency: string;
  openseaUrl: string;
  co2Offset?: {
    amount: number;
    unit: string;
    project: string;
  };
}

export default function NFTCollection() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNFT, setSelectedNFT] = useState<NFT | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showNoRecords, setShowNoRecords] = useState(true);

  // Toggle between showing NFTs and no records for demo purposes
  const toggleNFTDisplay = () => {
    setShowNoRecords(!showNoRecords);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-[88px]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Flight NFT Collection</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Own a piece of aviation history with our exclusive collection of flight NFTs. Each token represents a unique private jet journey, immortalized on the blockchain with verifiable carbon offset certificates.
            </p>
          </div>

          <div className="mb-8">
            <div className="max-w-xl mx-auto">
              <div className="relative">
                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by flight number, route, or aircraft type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>
            
            {/* Demo toggle button - would not exist in production */}
            <div className="mt-4 text-center">
              <button 
                onClick={toggleNFTDisplay} 
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg text-sm"
              >
                Toggle Demo
              </button>
            </div>
          </div>

          {showNoRecords ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane size={24} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">No NFTs Available Yet</h3>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                Our NFT collection is launching soon. Each flight will be minted as a unique NFT with verifiable carbon offset certificates.
              </p>
              <button 
                className="mt-6 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                onClick={toggleNFTDisplay}
              >
                View Preview
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {/* This is just a preview of what the NFT collection will look like */}
              <div 
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1 duration-200 flex flex-col"
                onClick={() => {
                  setSelectedNFT({
                    id: '1',
                    title: 'London to New York Private Flight',
                    description: 'Historic private charter flight from London Luton to New York JFK on a Gulfstream G650',
                    imageUrl: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80',
                    flightNumber: 'PCX123',
                    origin: 'EGGW',
                    destination: 'KJFK',
                    date: '2025-03-15',
                    duration: '7h 30m',
                    aircraft: 'Gulfstream G650',
                    price: 2.5,
                    currency: 'ETH',
                    openseaUrl: 'https://opensea.io/Privatecharterx',
                    co2Offset: {
                      amount: 12.5,
                      unit: 'tons',
                      project: 'Amazon Rainforest Conservation'
                    }
                  });
                  setShowModal(true);
                }}
              >
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80"
                    alt="London to New York Private Flight"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                    2.5 ETH
                  </div>
                  <div className="absolute bottom-2 left-2 bg-green-500/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18.364 18.364A9 9 0 0 0 5.636 5.636"></path>
                      <path d="M12 18a6 6 0 0 0-6-6"></path>
                      <path d="M12 12a3 3 0 0 0-3-3"></path>
                      <line x1="12" y1="21" x2="12" y2="12"></line>
                    </svg>
                    <span>Carbon Offset</span>
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-bold text-sm mb-2 line-clamp-1">London to New York Private Flight</h3>
                  <div className="flex items-center text-gray-600 text-xs mb-2">
                    <MapPin size={12} className="mr-1 flex-shrink-0" />
                    <span className="truncate">EGGW → KJFK</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <div className="flex items-center text-xs bg-gray-100 px-2 py-1 rounded-full">
                      <Plane size={10} className="mr-1" />
                      <span>PCX123</span>
                    </div>
                    <div className="flex items-center text-xs bg-gray-100 px-2 py-1 rounded-full">
                      <Clock size={10} className="mr-1" />
                      <span>7h 30m</span>
                    </div>
                  </div>
                  <div className="mt-auto">
                    <div className="w-full flex items-center justify-center gap-1 bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium">
                      View Details
                      <ExternalLink size={12} />
                    </div>
                  </div>
                </div>
              </div>
              
              <div 
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1 duration-200 flex flex-col"
                onClick={() => {
                  setSelectedNFT({
                    id: '2',
                    title: 'Dubai to Maldives Luxury Flight',
                    description: 'Exclusive private jet journey from Dubai to Male on a Bombardier Global 7500',
                    imageUrl: 'https://images.unsplash.com/photo-1583373834259-46cc92173cb7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80',
                    flightNumber: 'PCX456',
                    origin: 'OMDB',
                    destination: 'VRMM',
                    date: '2025-03-16',
                    duration: '4h 15m',
                    aircraft: 'Bombardier Global 7500',
                    price: 1.8,
                    currency: 'ETH',
                    openseaUrl: 'https://opensea.io/Privatecharterx',
                    co2Offset: {
                      amount: 8.2,
                      unit: 'tons',
                      project: 'Solar Energy Development'
                    }
                  });
                  setShowModal(true);
                }}
              >
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1583373834259-46cc92173cb7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80"
                    alt="Dubai to Maldives Luxury Flight"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                    1.8 ETH
                  </div>
                  <div className="absolute bottom-2 left-2 bg-green-500/80 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18.364 18.364A9 9 0 0 0 5.636 5.636"></path>
                      <path d="M12 18a6 6 0 0 0-6-6"></path>
                      <path d="M12 12a3 3 0 0 0-3-3"></path>
                      <line x1="12" y1="21" x2="12" y2="12"></line>
                    </svg>
                    <span>Carbon Offset</span>
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-bold text-sm mb-2 line-clamp-1">Dubai to Maldives Luxury Flight</h3>
                  <div className="flex items-center text-gray-600 text-xs mb-2">
                    <MapPin size={12} className="mr-1 flex-shrink-0" />
                    <span className="truncate">OMDB → VRMM</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    <div className="flex items-center text-xs bg-gray-100 px-2 py-1 rounded-full">
                      <Plane size={10} className="mr-1" />
                      <span>PCX456</span>
                    </div>
                    <div className="flex items-center text-xs bg-gray-100 px-2 py-1 rounded-full">
                      <Clock size={10} className="mr-1" />
                      <span>4h 15m</span>
                    </div>
                  </div>
                  <div className="mt-auto">
                    <div className="w-full flex items-center justify-center gap-1 bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium">
                      View Details
                      <ExternalLink size={12} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* NFT Details Modal */}
          {showModal && selectedNFT && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="bg-white rounded-2xl max-w-3xl w-full my-8">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative h-[300px] lg:h-[500px]">
                    <img 
                      src={selectedNFT.imageUrl}
                      alt={selectedNFT.title}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => {
                        setShowModal(false);
                        setSelectedNFT(null);
                      }}
                      className="absolute top-4 right-4 p-2 bg-black/80 text-white rounded-full hover:bg-black/90 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="p-6 max-h-[500px] overflow-y-auto">
                    <div className="mb-6">
                      <div className="flex justify-between items-start gap-4">
                        <h2 className="text-2xl font-bold">{selectedNFT.title}</h2>
                        <div className="text-right">
                          <div className="text-2xl font-bold">{selectedNFT.price} {selectedNFT.currency}</div>
                          <div className="text-xs text-gray-500">Current Price</div>
                        </div>
                      </div>
                      <p className="text-gray-600 mt-3 text-sm">{selectedNFT.description}</p>
                    </div>

                    <div className="space-y-6 mb-6">
                      <div>
                        <h3 className="text-lg font-bold mb-3">Flight Details</h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-xs text-gray-500">Flight Number</div>
                            <div className="font-medium text-sm">{selectedNFT.flightNumber}</div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-xs text-gray-500">Date</div>
                            <div className="font-medium text-sm">{formatDate(selectedNFT.date)}</div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-xs text-gray-500">Duration</div>
                            <div className="font-medium text-sm">{selectedNFT.duration}</div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-xs text-gray-500">Aircraft</div>
                            <div className="font-medium text-sm">{selectedNFT.aircraft}</div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-bold mb-3">Route Information</h3>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs text-gray-500">Origin</div>
                              <div className="font-medium text-sm">{selectedNFT.origin}</div>
                            </div>
                            <ArrowRight size={20} className="text-gray-400" />
                            <div>
                              <div className="text-xs text-gray-500">Destination</div>
                              <div className="font-medium text-sm">{selectedNFT.destination}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {selectedNFT.co2Offset && (
                        <div>
                          <h3 className="text-lg font-bold mb-3">Carbon Offset</h3>
                          <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                            <div className="flex items-center gap-2 mb-2">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                                <path d="M18.364 18.364A9 9 0 0 0 5.636 5.636"></path>
                                <path d="M12 18a6 6 0 0 0-6-6"></path>
                                <path d="M12 12a3 3 0 0 0-3-3"></path>
                                <line x1="12" y1="21" x2="12" y2="12"></line>
                              </svg>
                              <span className="font-medium text-green-700">Verified Carbon Offset</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <div className="text-xs text-gray-500">Amount</div>
                                <div className="font-medium text-green-700">{selectedNFT.co2Offset.amount} {selectedNFT.co2Offset.unit}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Project</div>
                                <div className="font-medium text-green-700">{selectedNFT.co2Offset.project}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <a
                      href={selectedNFT.openseaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors text-sm"
                    >
                      View on OpenSea
                      <ExternalLink size={16} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}